import { useEffect, useState } from "react";
import { adminGet } from "@/lib/adminApi";

const DAY_START = 8 * 60; // 08h00
const DAY_END = 20 * 60; // 20h00
const STEP = 30;
const DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const hm = (v) => parseInt(v.split(":")[0], 10) * 60 + parseInt(v.split(":")[1], 10);
const toMin = (t) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - day + 1);
  return d;
}

export default function WeekGrid({ planning, rdv, defaultOffset = 1, onSelectBooking }) {
  const [own, setOwn] = useState(null);
  const [offset, setOffset] = useState(defaultOffset); // 1 = semaine prochaine par défaut

  useEffect(() => {
    if (planning && rdv) return;
    Promise.all([adminGet("/planning"), adminGet("/rdv")])
      .then(([p, r]) => setOwn({ planning: p.planning, rdv: r.rdv }))
      .catch(() => setOwn({ planning: [], rdv: [] }));
  }, [planning, rdv]);

  const plages = planning || own?.planning || [];
  const bookings = (rdv || own?.rdv || []).filter((r) => !(r.status || "").startsWith("annulé"));

  const monday = mondayOf(new Date());
  monday.setDate(monday.getDate() + offset * 7);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });

  const cellState = (dateStr, t) => {
    const b = bookings.find((r) => r.date === dateStr && hm(r.start) <= t && t < hm(r.start) + (r.duration || 45));
    if (b) return { cls: "is-booked", label: hm(b.start) === t ? b.name || "Réservé" : "", booking: b };
    const busy = plages.find((p) => p.kind === "occupe" && p.date === dateStr && hm(p.start) <= t && t < hm(p.end));
    if (busy) return { cls: "is-busy", label: hm(busy.start) === t ? busy.label || "Occupé" : "" };
    const blocked = plages.find((p) => p.kind === "bloque" && p.date === dateStr && hm(p.start) <= t && t < hm(p.end));
    if (blocked) return { cls: "is-blocked", label: hm(blocked.start) === t ? blocked.label || "Indisponible" : "" };
    const free = plages.find((p) => p.kind === "dispo" && p.date === dateStr && hm(p.start) <= t && t < hm(p.end));
    if (free) return { cls: "is-free", label: hm(free.start) === t ? "Libre" : "" };
    return { cls: "", label: "" };
  };

  const times = [];
  for (let t = DAY_START; t < DAY_END; t += STEP) times.push(t);

  return (
    <div className="rav-week" data-testid="planning-week">
      <div className="rav-week-nav">
        <button type="button" className="rav-text-link" data-testid="week-prev" onClick={() => setOffset((o) => o - 1)}>
          ‹ Semaine précédente
        </button>
        <span className="rav-week-title" data-testid="week-title">
          Semaine du {days[0].getDate()}/{String(days[0].getMonth() + 1).padStart(2, "0")} au {days[6].getDate()}/
          {String(days[6].getMonth() + 1).padStart(2, "0")}
          {offset === 0 ? " (cette semaine)" : offset === 1 ? " (semaine prochaine)" : ""}
        </span>
        <button type="button" className="rav-text-link" data-testid="week-next" onClick={() => setOffset((o) => o + 1)}>
          Semaine suivante ›
        </button>
      </div>
      <div className="rav-week-scroll">
        <table className="rav-week-table">
          <thead>
            <tr>
              <th className="rav-week-time-col" />
              {days.map((d, i) => (
                <th key={i} data-testid={`week-day-${i}`}>
                  {DAYS_SHORT[i]} {d.getDate()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {times.map((t) => (
              <tr key={t}>
                <td className="rav-week-time-col">{t % 60 === 0 ? `${t / 60}h` : ""}</td>
                {days.map((d, i) => {
                  const c = cellState(iso(d), t);
                  return (
                    <td
                      key={i}
                      className={`rav-week-cell ${c.cls}${c.booking && onSelectBooking ? " is-clickable" : ""}`}
                      data-testid={`week-cell-${iso(d)}-${toMin(t)}`}
                      title={c.booking ? `${c.booking.name || "Réservé"} — cliquer pour ouvrir la fiche` : c.label}
                      onClick={c.booking && onSelectBooking ? () => onSelectBooking(c.booking) : undefined}
                    >
                      {c.label}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="rav-week-legend">
        <span className="rav-week-dot is-free" /> Libre (réservable en ligne) · <span className="rav-week-dot is-booked" /> Réservé (nom de la personne) ·{" "}
        <span className="rav-week-dot is-busy" /> Occupé (clientèle locale) · <span className="rav-week-dot is-blocked" /> Indisponible (bloqué par Julie)
      </p>
    </div>
  );
}
